/** Default Claude upload pipeline — keeps token cost down without UI changes. */
export const CLIENT_IMAGE_MAX_WIDTH_FOR_API = 1200;
export const CLIENT_IMAGE_JPEG_QUALITY = 0.8;

/**
 * Client-only: resize wide images and re-encode as JPEG before sending to Claude (Canvas API).
 */
export async function compressImageBase64ForApi(
  base64: string,
  mediaType: string,
  options?: { maxWidth?: number; quality?: number },
): Promise<{ base64: string; mediaType: string }> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return { base64, mediaType };
  }

  const maxWidth = options?.maxWidth ?? CLIENT_IMAGE_MAX_WIDTH_FOR_API;
  const quality = options?.quality ?? CLIENT_IMAGE_JPEG_QUALITY;
  const mime = mediaType?.startsWith("image/") ? mediaType : "image/jpeg";

  return new Promise((resolve, reject) => {
    const img = new Image();
    let objectUrl: string | null = null;

    const finish = () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };

    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        if (!w || !h) {
          finish();
          resolve({ base64, mediaType: mime });
          return;
        }
        const scale = w > maxWidth ? maxWidth / w : 1;
        const dw = Math.max(1, Math.round(w * scale));
        const dh = Math.max(1, Math.round(h * scale));
        const canvas = document.createElement("canvas");
        canvas.width = dw;
        canvas.height = dh;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          finish();
          resolve({ base64, mediaType: mime });
          return;
        }
        ctx.drawImage(img, 0, 0, dw, dh);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        const comma = dataUrl.indexOf(",");
        finish();
        resolve({
          base64: dataUrl.slice(comma + 1),
          mediaType: "image/jpeg",
        });
      } catch (e) {
        finish();
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };

    img.onerror = () => {
      finish();
      reject(new Error("Image failed to load for compression"));
    };

    const dataUrl = `data:${mime};base64,${base64}`;
    void fetch(dataUrl)
      .then((res) => res.blob())
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        img.src = objectUrl;
      })
      .catch(() => {
        img.src = dataUrl;
      });
  });
}
